import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import Snackbar from "./Snackbar";

interface SearchProps {
  data: any[];
  searchKeys: string[];
  onSearchResult: (filteredData: any[]) => void;
  placeholder?: string;
  onSearchQueryChange?: (query: string) => void;
  onInputChange?: (query: string) => void;
  activitySearchWidth?: boolean;
}

// Define the type for the ref object
interface SearchInputHandles {
  focus: () => void;
  clear: () => void;
}

const SearchInput = forwardRef<SearchInputHandles, SearchProps>(
  (
    {
      data,
      searchKeys,
      onSearchResult,
      placeholder,
      onSearchQueryChange,
      onInputChange,
      activitySearchWidth = false,
    },
    ref,
  ) => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);
    // Expose functions to the parent using useImperativeHandle
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      clear: () => {
        setSearchTerm("");
        if (onSearchQueryChange) {
          onSearchQueryChange("");
        }
        onSearchResult(data);
      },
    }));
    const specialCharacterRegex = /[^a-zA-Z0-9\s]/; // Regex for special characters

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (onInputChange) {
        onInputChange(value);
      }
      // Check for special characters
      // if (specialCharacterRegex.test(value)) {
      //   setSnackbarMessage("Special characters are not allowed!");
      //   setSnackbarOpen(true);
      //   return;
      // }
      // Update searchTerm only for 3+ characters
      if (value.length >= 3) {
        setSearchTerm(value);
        if (onSearchQueryChange) {
          onSearchQueryChange(value);
        }

        const filteredData = data.filter((row) =>
          searchKeys.some((key) =>
            row[key]?.toString().toLowerCase().includes(value.toLowerCase()),
          ),
        );
        onSearchResult(filteredData);
      } else if (value.trim() === "") {
        setSearchTerm("");
        if (onSearchQueryChange) {
          onSearchQueryChange("");
        }
        onSearchResult(data);
      } else {
        setSearchTerm(value);
        onSearchResult(data);
      }
    };

    const handleClear = () => {
      setSearchTerm("");
      if (onSearchQueryChange) {
        onSearchQueryChange("");
      }
      onSearchResult(data);
    };

    return (
      <div style={{ width: activitySearchWidth ? "80%" : "30%" }}>
        <div className="input-group d-flex" style={{ position: "relative" }}>
          <input
            type="text"
            className="me-auto"
            placeholder={placeholder || "Search..."}
            value={searchTerm}
            onChange={handleInputChange}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              border: "1px solid lightblue",
              borderRadius: "4px",
              paddingRight: "35px",
              position: "relative",
            }}
          />
          {/* Conditionally render the close icon */}
          {searchTerm && (
            <span
              className="material-icons material-symbols-outlined"
              onClick={handleClear}
              style={{
                right: "10px",
                position: "absolute",
                cursor: "pointer",
                color: "#aaa",
                top: "10px",
              }}
            >
              close
            </span>
          )}
        </div>

        {/* Snackbar for error message */}
        <Snackbar
          open={snackbarOpen}
          message={snackbarMessage}
          onClose={() => setSnackbarOpen(false)}
          severity="error"
        />
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";
export default SearchInput;
